// src/components/common/EmployeeOverviewDrawer.jsx
// ============================================================
// Employee Overview Drawer.
// Displays the full 20 sections of Employee Master data in tabs.
// Handles permissions and masking of sensitive details.
// ============================================================

import React, { useState, useEffect, useMemo } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  limit,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import { isPresent } from "../../utils/attendanceStatus";
import { useAuth } from "../../context/AuthContext";
import {
  useEmployeePersonalDetails,
  useEmployeeBankDetails,
  useEmployeeEducation,
  useEmployeeExperience,
  useEmployeeDocuments,
  useEmployeeTimeline,
} from "../../hooks/useEmployeeMaster";

// ── Real-time details hooks for standard data ─────

function useEmployeeAttendance(userId) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) { setData([]); setLoading(false); return; }
    setLoading(true);
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const from = `${now.getFullYear()}-${mm}-01`;
    const to = `${now.getFullYear()}-${mm}-31`;

    const q = query(
      collection(db, "attendance"),
      where("userId", "==", userId),
      where("date", ">=", from),
      where("date", "<=", to),
      orderBy("date", "desc"),
    );
    const unsub = onSnapshot(q, (snap) => {
      setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [userId]);

  return { data, loading };
}

function useEmployeeLeaves(userId) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) { setData([]); setLoading(false); return; }
    setLoading(true);
    const q = query(
      collection(db, "leaveRequests"),
      where("userId", "==", userId),
      orderBy("createdAt", "desc"),
      limit(20),
    );
    const unsub = onSnapshot(q, (snap) => {
      setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [userId]);

  return { data, loading };
}

function useEmployeeShiftAssignment(userId) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) { setData([]); setLoading(false); return; }
    setLoading(true);
    const q = query(
      collection(db, "shiftAssignments"),
      where("userId", "==", userId),
    );
    const unsub = onSnapshot(q, (snap) => {
      setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [userId]);

  return { data, loading };
}

function fmtDate(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export default function EmployeeOverviewDrawer({ user, shifts, allUsers, onClose }) {
  const { userProfile } = useAuth();
  const uid = user?.uid;

  const [activeTab, setActiveTab] = useState("overview");

  // Basic lists
  const { data: attendance, loading: attLoad } = useEmployeeAttendance(uid);
  const { data: leaves } = useEmployeeLeaves(uid);
  const { data: shiftAssignments, loading: shiftLoad } = useEmployeeShiftAssignment(uid);

  // Subcollections loaded from Employee Master module
  const { data: personal, loading: personalLoad } = useEmployeePersonalDetails(uid);
  const { data: bank, loading: bankLoad } = useEmployeeBankDetails(uid);
  const { data: education, loading: eduLoad } = useEmployeeEducation(uid);
  const { data: experience, loading: expLoad } = useEmployeeExperience(uid);
  const { data: documents, loading: docsLoad } = useEmployeeDocuments(uid);
  const { data: timeline, loading: timelineLoad } = useEmployeeTimeline(uid);

  // Permissions checks
  const isSelf = userProfile?.uid === uid;
  const isAdmin = userProfile?.role === "admin";
  const canViewSensitive = isAdmin || isSelf;

  const teamLead = useMemo(() => {
    if (!user?.teamLeadId && !user?.teamId) return null;
    const leadId = user.teamLeadId || user.teamId;
    return allUsers?.find((u) => u.uid === leadId) || null;
  }, [user, allUsers]);

  const currentAssignment = useMemo(() => {
    return (
      shiftAssignments.find((a) => a.status?.toUpperCase() === "APPROVED") ||
      shiftAssignments[0] ||
      (user?.shiftId ? { shiftId: user.shiftId, status: "ASSIGNED" } : null)
    );
  }, [shiftAssignments, user?.shiftId]);

  const currentShift = useMemo(() => {
    if (!currentAssignment) return null;
    return shifts?.find((s) => s.id === currentAssignment.shiftId) || null;
  }, [currentAssignment, shifts]);

  const attStats = useMemo(() => {
    const present = attendance.filter(isPresent).length;
    const late = attendance.filter((a) => a.isLate).length;
    const now = new Date();
    const dayOfMonth = now.getDate();
    let workDays = 0;
    for (let i = 1; i <= dayOfMonth; i++) {
      const d = new Date(now.getFullYear(), now.getMonth(), i);
      const dow = d.getDay();
      if (dow !== 0 && dow !== 6) workDays++;
    }
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    const approvedLeaveCount = leaves.filter(
      (l) => l.status === "approved" && l.fromDate <= todayStr && l.toDate >= todayStr
    ).length;
    const absent = Math.max(0, workDays - present - approvedLeaveCount);
    const pct = workDays > 0 ? Math.round((present / workDays) * 100) : 0;
    return { present, absent, late, pct, workDays, leavesTaken: approvedLeaveCount };
  }, [attendance, leaves]);

  if (!user) return null;

  // Masking helpers
  const maskAadhaar = (val) => {
    if (!val) return "—";
    if (canViewSensitive) return val.replace(/(\d{4})/g, "$1 ").trim();
    return "XXXX XXXX " + val.slice(-4);
  };

  const maskPAN = (val) => {
    if (!val) return "—";
    if (canViewSensitive) return val.toUpperCase();
    return "XXXXX" + val.slice(-5).toUpperCase();
  };

  const maskAccount = (val) => {
    if (!val) return "—";
    if (canViewSensitive) return val;
    return "XXXXXX" + val.slice(-4);
  };

  return (
    <>
      <style>{`
        .drawer-tabs-nav {
          display: flex;
          gap: 0.25rem;
          background: var(--bg-secondary);
          border: 1px solid var(--border);
          padding: 0.25rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
          overflow-x: auto;
        }
        .drawer-tab-btn {
          flex: 1;
          padding: 0.5rem 0.75rem;
          background: transparent;
          border: none;
          color: var(--text-muted);
          font-weight: 500;
          font-size: 0.78rem;
          cursor: pointer;
          border-radius: 6px;
          white-space: nowrap;
          transition: all 0.2s ease;
        }
        .drawer-tab-btn--active {
          background: var(--bg-primary);
          color: var(--brand-primary, #1b4b31);
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .history-timeline {
          position: relative;
          padding-left: 1.5rem;
          border-left: 2px solid var(--border);
          margin-left: 0.5rem;
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }
        .timeline-event {
          position: relative;
        }
        .timeline-dot {
          position: absolute;
          left: calc(-1.5rem - 6px);
          top: 4px;
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: var(--brand-primary, #1b4b31);
          border: 2px solid var(--bg-primary);
        }
        .timeline-time {
          font-size: 0.7rem;
          color: var(--text-muted);
        }
        .timeline-title {
          font-size: 0.8rem;
          font-weight: 600;
          margin: 2px 0;
        }
        .timeline-desc {
          font-size: 0.78rem;
          color: var(--text-muted);
        }
      `}</style>

      {/* Backdrop */}
      <div className="drawer-backdrop" onClick={onClose} />

      {/* Drawer */}
      <div className="employee-drawer">
        {/* Header */}
        <div className="drawer-header">
          <div className="drawer-header-info">
            <div className="drawer-avatar">
              {user.displayName?.slice(0, 2).toUpperCase()}
            </div>
            <div>
              <h2 className="drawer-name">{user.displayName}</h2>
              <p className="drawer-meta">
                {user.employeeId ? `${user.employeeId} • ` : ""}{user.role === "admin" ? "Admin" : user.role === "lead" ? "Team Lead" : "Employee"} • {user.department}
              </p>
            </div>
          </div>
          <button className="drawer-close" onClick={onClose}>✕</button>
        </div>

        {/* Navigation Tabs */}
        <div style={{ padding: "0 1.5rem" }}>
          <div className="drawer-tabs-nav">
            <button type="button" className={`drawer-tab-btn ${activeTab === "overview" ? "drawer-tab-btn--active" : ""}`} onClick={() => setActiveTab("overview")}>
              Overview
            </button>
            <button type="button" className={`drawer-tab-btn ${activeTab === "personal" ? "drawer-tab-btn--active" : ""}`} onClick={() => setActiveTab("personal")}>
              Personal
            </button>
            <button type="button" className={`drawer-tab-btn ${activeTab === "financial" ? "drawer-tab-btn--active" : ""}`} onClick={() => setActiveTab("financial")}>
              Financials
            </button>
            <button type="button" className={`drawer-tab-btn ${activeTab === "history" ? "drawer-tab-btn--active" : ""}`} onClick={() => setActiveTab("history")}>
              Timeline & Bio
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="drawer-body" style={{ paddingTop: "0" }}>

          {/* TAB 1: OVERVIEW */}
          {activeTab === "overview" && (
            <>
              {/* Basic Details */}
              <Section title="Employment Status" icon="📋">
                <InfoGrid>
                  <InfoItem label="Employee ID" value={user.employeeId || "—"} mono />
                  <InfoItem label="Official Email" value={user.email} />
                  <InfoItem label="Department" value={user.department || "—"} />
                  <InfoItem label="Role" value={
                    <span className={`status-pill status-pill--${
                      user.role === "admin" ? "blue" : user.role === "lead" ? "purple" : "gray"
                    }`}>
                      {user.role === "admin" ? "Admin" : user.role === "lead" ? "Team Lead" : "Employee"}
                    </span>
                  } />
                  {user.role === "employee" && (
                    <InfoItem label="Team Lead" value={teamLead ? teamLead.displayName : "Not assigned"} />
                  )}
                  <InfoItem label="Joined" value={
                    user.joiningDate
                      ? new Date(user.joiningDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
                      : (user.createdAt ? fmtDate(user.createdAt) : "Not Available")
                  } />
                  <InfoItem label="Status" value={
                    <span className={`status-pill status-pill--${user.isActive ? "green" : "red"}`}>
                      {user.isActive ? "Active" : "Inactive"}
                    </span>
                  } />
                </InfoGrid>
              </Section>

              {/* Shift Details */}
              <Section title="Shift Details" icon="🕒">
                {shiftLoad ? <MiniLoader /> : currentShift ? (
                  <InfoGrid>
                    <InfoItem label="Current Shift" value={<span style={{ fontWeight: 700 }}>{currentShift.name}</span>} />
                    <InfoItem label="Timing" value={`${currentShift.startTime} — ${currentShift.endTime}`} mono />
                    <InfoItem label="Status" value={<ShiftStatusPill status={currentAssignment?.status} />} />
                    <InfoItem label="Assigned By" value={currentAssignment?.assignedByRole === "admin" ? "Admin" : "Team Lead"} />
                  </InfoGrid>
                ) : (
                  <p className="drawer-empty">No shift assigned</p>
                )}
              </Section>

              {/* Attendance */}
              <Section title="Attendance (This Month)" icon="📊">
                {attLoad ? <MiniLoader /> : (
                  <>
                    <div className="stat-row">
                      <StatBox label="Present" value={attStats.present} color="var(--green)" />
                      <StatBox label="Absent" value={attStats.absent} color="var(--red)" />
                      <StatBox label="Late" value={attStats.late} color="var(--amber)" />
                      <StatBox label="Rate" value={`${attStats.pct}%`} color="var(--brand-primary, #1b4b31)" />
                    </div>
                  </>
                )}
              </Section>
            </>
          )}

          {/* TAB 2: PERSONAL PROFILE */}
          {activeTab === "personal" && (
            <>
              {personalLoad ? <MiniLoader /> : (
                <>
                  <Section title="Personal Identification" icon="👤">
                    <InfoGrid>
                      <InfoItem label="Father's Name" value={personal?.fatherName || "—"} />
                      <InfoItem label="Mother's Name" value={personal?.motherName || "—"} />
                      <InfoItem label="Spouse Name" value={personal?.spouseName || "—"} />
                      <InfoItem label="Aadhaar Card" value={maskAadhaar(personal?.aadhaar)} mono />
                      <InfoItem label="PAN Card" value={maskPAN(personal?.pan)} mono />
                      <InfoItem label="Passport" value={canViewSensitive ? (personal?.passport || "—") : "Protected"} mono />
                      <InfoItem label="Gender" value={user.gender || "—"} />
                      <InfoItem label="DOB" value={user.dateOfBirth ? new Date(user.dateOfBirth).toLocaleDateString() : "—"} />
                      <InfoItem label="Marital Status" value={user.maritalStatus || "—"} />
                      <InfoItem label="Blood Group" value={user.bloodGroup || "—"} />
                      <InfoItem label="Nationality" value={user.nationality || "—"} />
                    </InfoGrid>
                  </Section>

                  <Section title="Addresses" icon="📍">
                    <h5 style={{ margin: "0.5rem 0", fontSize: "0.8rem", color: "var(--brand-primary, #1b4b31)" }}>Permanent Address</h5>
                    <p style={{ fontSize: "0.8rem", margin: "0 0 1rem 0" }}>
                      {personal?.permanentAddress?.line1 ? (
                        `${personal.permanentAddress.line1}, ${personal.permanentAddress.line2 || ""}, ${personal.permanentAddress.city}, ${personal.permanentAddress.state}, ${personal.permanentAddress.country} - ${personal.permanentAddress.pincode}`
                      ) : "—"}
                    </p>
                    <h5 style={{ margin: "0.5rem 0", fontSize: "0.8rem", color: "var(--brand-primary, #1b4b31)" }}>Current Address</h5>
                    <p style={{ fontSize: "0.8rem", margin: 0 }}>
                      {personal?.currentAddress?.sameAsPermanent ? (
                        "Same as Permanent Address"
                      ) : personal?.currentAddress?.line1 ? (
                        `${personal.currentAddress.line1}, ${personal.currentAddress.line2 || ""}, ${personal.currentAddress.city}, ${personal.currentAddress.state}, ${personal.currentAddress.country} - ${personal.currentAddress.pincode}`
                      ) : "—"}
                    </p>
                  </Section>

                  <Section title="Emergency Contact" icon="🚨">
                    <InfoGrid>
                      <InfoItem label="Emergency Number" value={user.emergencyContact || "—"} mono />
                    </InfoGrid>
                  </Section>
                </>
              )}
            </>
          )}

          {/* TAB 3: FINANCIALS */}
          {activeTab === "financial" && (
            <>
              {!canViewSensitive ? (
                <div style={{ padding: "2rem 1rem", textAlign: "center", color: "var(--text-muted)" }}>
                  🔒 You do not have permissions to view financial and payroll information.
                </div>
              ) : bankLoad ? <MiniLoader /> : (
                <>
                  <Section title="Bank Details" icon="🏦">
                    <InfoGrid>
                      <InfoItem label="Bank Name" value={bank?.bankName || "—"} />
                      <InfoItem label="Branch" value={bank?.branch || "—"} />
                      <InfoItem label="Account Number" value={maskAccount(bank?.accountNumber)} mono />
                      <InfoItem label="IFSC Code" value={bank?.ifscCode || "—"} mono />
                      <InfoItem label="Account Holder" value={bank?.accountHolder || "—"} />
                      <InfoItem label="UPI ID" value={bank?.upiId || "—"} />
                    </InfoGrid>
                  </Section>

                  <Section title="Payroll & Statutory" icon="💸">
                    <InfoGrid>
                      <InfoItem label="Annual CTC" value={bank?.ctc ? `${Number(bank.ctc).toLocaleString("en-IN")} INR` : "—"} />
                      <InfoItem label="Basic Salary" value={bank?.basicSalary ? `${Number(bank.basicSalary).toLocaleString("en-IN")} INR` : "—"} />
                      <InfoItem label="HRA" value={bank?.hra ? `${Number(bank.hra).toLocaleString("en-IN")} INR` : "—"} />
                      <InfoItem label="Tax Regime" value={bank?.taxRegime || "—"} />
                      <InfoItem label="PF Applicable" value={bank?.pfApplicable ? "Yes" : "No"} />
                      <InfoItem label="PF Number" value={bank?.pfNumber || "—"} mono />
                      <InfoItem label="ESI Applicable" value={bank?.esiApplicable ? "Yes" : "No"} />
                      <InfoItem label="ESI Number" value={bank?.esiNumber || "—"} mono />
                      <InfoItem label="Professional Tax" value={bank?.professionalTax ? "Yes" : "No"} />
                      <InfoItem label="Labour Welfare Fund" value={bank?.lwf ? "Yes" : "No"} />
                    </InfoGrid>
                  </Section>
                </>
              )}
            </>
          )}

          {/* TAB 4: BIO & TIMELINE */}
          {activeTab === "history" && (
            <>
              {eduLoad || expLoad || timelineLoad ? <MiniLoader /> : (
                <>
                  {/* Education */}
                  <Section title="Academic History" icon="🎓">
                    {education.length === 0 ? <p className="drawer-empty">No academic records found</p> : (
                      <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                        {education.map((e) => (
                          <div key={e.id} style={{ padding: "0.5rem", background: "var(--bg-secondary)", borderRadius: "4px" }}>
                            <div style={{ fontWeight: 600, fontSize: "0.82rem" }}>{e.qualification}</div>
                            <div style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>{e.college} ({e.university})</div>
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.72rem", color: "var(--text-muted)", marginTop: "4px" }}>
                              <span>Passing Year: {e.passingYear}</span>
                              <span style={{ fontWeight: 600 }}>{e.percentage ? `${e.percentage}%` : e.grade}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Section>

                  {/* Prior Experience */}
                  <Section title="Prior Work Experience" icon="💼">
                    {experience.length === 0 ? <p className="drawer-empty">No prior experience logged</p> : (
                      <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                        {experience.map((e) => (
                          <div key={e.id} style={{ padding: "0.5rem", background: "var(--bg-secondary)", borderRadius: "4px" }}>
                            <div style={{ fontWeight: 600, fontSize: "0.82rem" }}>{e.designation}</div>
                            <div style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>{e.company}</div>
                            <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: "4px" }}>
                              {e.startDate} to {e.endDate || "Present"} ({e.yearsOfExperience} years)
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Section>

                  {/* Documents */}
                  <Section title="Uploaded Records" icon="📁">
                    {docsLoad ? <MiniLoader /> : documents.length === 0 ? <p className="drawer-empty">No documents uploaded</p> : (
                      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                        {documents.map((d) => (
                          <div key={d.id} style={{ padding: "0.5rem", border: "1px solid var(--border)", borderRadius: "4px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontSize: "0.8rem", fontWeight: 500 }}>{d.documentType}</span>
                            <span style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>v{d.version || 1}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </Section>

                  {/* Timeline */}
                  <Section title="Audit Timeline" icon="🕒">
                    {timeline.length === 0 ? <p className="drawer-empty">No timeline events logged</p> : (
                      <div className="history-timeline">
                        {timeline.map((event) => (
                          <div key={event.id} className="timeline-event">
                            <div className="timeline-dot" />
                            <div className="timeline-time">{event.timestamp ? fmtDate(event.timestamp) : "—"}</div>
                            <div className="timeline-title">{event.title}</div>
                            <div className="timeline-desc">{event.description}</div>
                            {event.changedBy && <div style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>Actor: {event.changedBy}</div>}
                          </div>
                        ))}
                      </div>
                    )}
                  </Section>
                </>
              )}
            </>
          )}

        </div>
      </div>
    </>
  );
}

// ── Sub-components ──────────────────────────────────────────

function Section({ title, icon, children }) {
  return (
    <div className="drawer-section">
      <h3 className="drawer-section-title" style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.95rem" }}>
        <span>{icon}</span> {title}
      </h3>
      {children}
    </div>
  );
}

function InfoGrid({ children }) {
  return <div className="drawer-info-grid">{children}</div>;
}

function InfoItem({ label, value, mono }) {
  return (
    <div className="drawer-info-item">
      <span className="drawer-info-label">{label}</span>
      <span className={`drawer-info-value${mono ? " mono" : ""}`}>
        {value}
      </span>
    </div>
  );
}

function StatBox({ label, value, color }) {
  return (
    <div className="drawer-stat-box">
      <span className="drawer-stat-value" style={{ color }}>{value}</span>
      <span className="drawer-stat-label">{label}</span>
    </div>
  );
}

function ShiftStatusPill({ status }) {
  if (!status) return <span className="status-pill status-pill--gray">NONE</span>;
  let color = "gray";
  if (status === "APPROVED") color = "green";
  if (status.includes("PENDING")) color = "amber";
  if (status === "REJECTED") color = "red";
  return (
    <span className={`status-pill status-pill--${color}`}>
      {status.replace(/_/g, " ")}
    </span>
  );
}

function MiniLoader() {
  return <div className="drawer-mini-loader">Loading…</div>;
}
